import { ApiResponseType, jsonInit } from '@shigo-ui/api-client';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  livePreviewResponseSchema,
  networkTemplateConfigurationSchema,
  networkTemplateUpdateResponseSchema,
  qrCodeAssetSchema,
  type A2vConfiguration,
  type A2vLivePreviewRequest,
} from './model';

interface NetworkTemplateClientOptions {
  config: ShigoApiConfig & {
    a2vUrl: string;
  };
  accessToken: string;
  fetcher?: typeof fetch;
}

export function createNetworkTemplateClient({
  config,
  accessToken,
  fetcher,
}: NetworkTemplateClientOptions) {
  const mainApi = createShigoApiClient(config, accessToken, fetcher);
  const livePreviewApi = createShigoApiClient(
    { apiBaseUrl: config.a2vUrl, networkId: config.networkId },
    accessToken,
    fetcher,
  );

  return {
    getConfiguration: () =>
      mainApi.request({
        path: '/api/admin/networks/configuration',
        schema: networkTemplateConfigurationSchema,
      }),
    updateConfiguration: async (configuration: A2vConfiguration) => {
      const response = await mainApi.request({
        path: '/api/admin/networks/configuration',
        schema: networkTemplateUpdateResponseSchema,
        init: jsonInit('PUT', configuration),
      });
      if (!response) return configuration;
      return response;
    },
    getPublisherConfiguration: (publisherId: string) =>
      mainApi.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/configuration`,
        schema: networkTemplateConfigurationSchema,
      }),
    updatePublisherConfiguration: async (
      publisherId: string,
      payload: {
        a2vConfiguration: A2vConfiguration;
        useCustomTemplate: boolean;
        publisher?: unknown;
      },
    ) => {
      const response = await mainApi.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/configuration`,
        schema: networkTemplateUpdateResponseSchema,
        init: jsonInit('PUT', payload),
      });
      return response ?? payload.a2vConfiguration;
    },
    generateQrCode: (text: string) =>
      mainApi.request({
        path: '/api/assets/qrcode',
        schema: qrCodeAssetSchema,
        init: jsonInit('POST', { text }),
        responseType: ApiResponseType.Text,
      }),
    createLivePreview: (request: A2vLivePreviewRequest) =>
      livePreviewApi.request({
        path: '/a2v',
        schema: livePreviewResponseSchema,
        init: jsonInit('POST', request),
      }),
  };
}
